import { gql } from '@apollo/client';

export const GET_ENROLLMENT_PERIODS_BY_GROUP_ID = gql`
  query ENROLLMENT_SERVICE_getAllEnrollmentPeriodsByGroupId($groupId: ID!, $showInstalled: Boolean) {
    getAllEnrollmentPeriodsByGroupId(groupId: $groupId, showInstalled: $showInstalled) {
      id
      groupId
      parentGroupId
      effectiveDate
      productLineIds
      stage
      proposals {
        status
        cartId
        cartVersion
        name
        createdOn
        createdBy {
          id
          name
        }
        productLines {
          id
          productType
          confirmedPortfolioId
          browsingCriteria
          carrierIds
        }
        confirmed
        preferred
        rateSegment
      }
    }
  }
`;

export const GET_ENROLLMENTS_BY_GROUP_IDS = gql`
  query ENROLLMENT_SERVICE_getEnrollmentsByGroupIds($groupIds: [ID]!, $showInstalled: Boolean) {
    getEnrollmentsByGroupIds(groupIds: $groupIds, showInstalled: $showInstalled) {
      id
      groupId
      effectiveDate
      productLineIds
      stage
      proposals {
        status
      }
    }
  }
`;

export const GET_ALL_PROPOSALS_FROM_ALL_ENROLLMENT_PERIODS_BY_GROUP_ID = gql`
  query ENROLLMENT_SERVICE_getAllProposalsByGroupId($groupId: ID!, $showInstalled: Boolean) {
    getAllEnrollmentPeriodsByGroupId(groupId: $groupId, showInstalled: $showInstalled) {
      id
      groupId
      parentGroupId
      hasConfirmedProposal
      effectiveDate
      productLineIds
      type
      stage
      rateSegment
      proposals {
        status
        quotingStatus
        cartId
        cartVersion
        name
        createdOn
        createdBy {
          id
          name
        }
        productLines {
          id
          productType
          confirmedPortfolioId
          browsingCriteria
          carrierIds
        }
        confirmed
        preferred
        rateSegment
      }
      installedProposal {
        productLines {
          type
          id
          terminationEffectiveDate
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const GET_INACTIVE_ENROLLMENT_PERIODS_BY_GROUP_ID = gql`
  query ENROLLMENT_SERVICE_getInactiveEnrollmentsByGroupId($groupId: ID!) {
    getInactiveEnrollmentsByGroupId(groupId: $groupId) {
      id
      groupId
      hasConfirmedProposal
      effectiveDate
      type
      stage
      rateSegment
      proposals {
        status
        cartId
        cartVersion
        name
        createdOn
        createdBy {
          id
          name
        }
        productLines {
          id
          productType
          confirmedPortfolioId
          browsingCriteria
        }
        confirmed
        preferred
        rateSegment
      }
      installedProposal {
        productLines {
          type
          id
          terminationEffectiveDate
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const CALCULATE_GROUP_PANEL_STAGES = gql`
  query ENROLLMENT_SERVICE_calculateCardValuesForGroupStageStatus($fullView: Boolean) {
    calculateCardValuesForGroupStageStatus(fullView: $fullView)
  }
`;

export const GET_ENROLLMENT_PERIOD_BY_ID = gql`
  query ENROLLMENT_SERVICE_getEnrollmentPeriod($periodId: ID!) {
    getEnrollmentPeriod(periodId: $periodId) {
      id
      groupId
      parentGroupId
      effectiveDate
      hasConfirmedProposal
      stage
      type
      rateSegment
      sponsorId: domainId
      proposals {
        status
        cartId
        browsingCriteria
        cartVersion
        name
        createdOn
        createdBy {
          id
          name
        }
        productLines {
          id
          productType
          confirmedPortfolioId
          browsingCriteria
        }
        confirmed
        preferred
        rateSegment
        initiateFormAnswers
        checklist {
          checklistOptions {
            card
            buttonState
            completionStatus
          }
        }
      }
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      postSubmitChecklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      productLineIds
      installedProposal {
        productLines {
          type
          id
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const SAVE_PROPOSAL = gql`
  mutation ENROLLMENT_SERVICE_saveProposal($proposal: InputProposalV2, $rateSegment: RateSegment, $sponsorId: String) {
    saveProposal(proposal: $proposal, rateSegment: $rateSegment, sponsorId: $sponsorId) {
      id
      rateSegment
      effectiveDate
      groupId
      installedProposal {
        productLines {
          type
          id
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const SAVE_PROPOSAL_AND_COMPLETE_STEP = gql`
  mutation ENROLLMENT_SERVICE_saveProposalAndCompleteStep(
    $proposal: InputProposalV2
    $rateSegment: RateSegment
    $sponsorId: String
    $periodId: ID!
    $cartId: String!
    $userAction: UserActions
  ) {
    saveProposal(proposal: $proposal, rateSegment: $rateSegment, sponsorId: $sponsorId) {
      id
      groupId
      effectiveDate
      rateSegment
      installedProposal {
        productLines {
          type
          id
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
    applyProposalUserAction(periodId: $periodId, cartId: $cartId, userAction: $userAction) {
      id
    }
  }
`;

export const CONFIRM_PROPOSAL = gql`
  mutation ENROLLMENT_SERVICE_confirmProposal(
    $periodId: ID
    $proposal: InputProposalV2
    $proposalAction: ProposalAction
  ) {
    confirmProposal(periodId: $periodId, proposal: $proposal, proposalAction: $proposalAction) {
      id
    }
  }
`;

export const SAVE_PROPOSAL_CHOICES = gql`
  mutation ENROLLMENT_SERVICE_saveProposalChoices($periodId: ID!, $proposal: InputProposalV2!) {
    saveProposalChoices(periodId: $periodId, proposal: $proposal) {
      id
    }
  }
`;

export const DELETE_PROPOSAL = gql`
  mutation ENROLLMENT_SERVICE_deleteProposal($periodId: ID, $cartId: ID) {
    deleteProposal(periodId: $periodId, cartId: $cartId) {
      id
      groupId
      hasConfirmedProposal
      effectiveDate
      type
      stage
      rateSegment
      proposals {
        status
        cartId
        cartVersion
        name
        createdOn
        createdBy {
          id
          name
        }
        productLines {
          id
          productType
          confirmedPortfolioId
          browsingCriteria
        }
        confirmed
        preferred
        rateSegment
      }
    }
  }
`;

export const APPLY_USER_ACTION = gql`
  mutation ENROLLMENT_SERVICE_applyUserAction($periodId: ID, $userAction: UserActions) {
    applyUserAction(periodId: $periodId, userAction: $userAction) {
      id
    }
  }
`;

export const APPLY_POST_SUBMIT_USER_ACTION = gql`
  mutation ENROLLMENT_SERVICE_applyPostSubmitUserAction($periodId: ID!, $userAction: UserActions!) {
    applyPostSubmitUserAction(periodId: $periodId, userAction: $userAction) {
      id
    }
  }
`;

export const BUILD_ENROLLMENT = gql`
  mutation ENROLLMENT_SERVICE_completeEnrollment($periodId: ID!, $submittedByUserName: String, $finalBC: Map) {
    completeEnrollment(periodId: $periodId, submittedByUserName: $submittedByUserName, finalBC: $finalBC) {
      id
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
    }
  }
`;

export const ROLLBACK_ENROLLMENT = gql`
  mutation ENROLLMENT_SERVICE_rollbackEnrollment($periodId: ID!) {
    rollbackEnrollment(periodId: $periodId) {
      id
    }
  }
`;

export const SELL_ENROLLMENT = gql`
  mutation ENROLLMENT_SERVICE_sellEnrollment($periodId: ID!) {
    sellEnrollment(periodId: $periodId) {
      id
    }
  }
`;

export const UPDATE_PROPOSAL_NAME = gql`
  mutation ENROLLMENT_SERVICE_updateProposalName($periodId: ID!, $cartId: ID!, $name: String!) {
    updateProposalName(periodId: $periodId, cartId: $cartId, name: $name)
  }
`;

export const TRIGGER_RENEWAL = gql`
  mutation ENROLLMENT_SERVICE_triggerRenewal(
    $periodId: ID!
    $emailReceivers: [EmailReceiver!]! = []
    $sendEmails: Boolean = true
  ) {
    triggerRenewal(periodId: $periodId, emailReceivers: $emailReceivers, sendEmails: $sendEmails) {
      id
    }
  }
`;

export const DELETE_ENROLLMENT = gql`
  mutation ENROLLMENT_SERVICE_deletePeriod($periodId: ID!) {
    deletePeriod(periodId: $periodId) {
      id
    }
  }
`;

export const DELETE_ENROLLMENTS = gql`
  mutation ENROLLMENT_SERVICE_deletePeriods($periodIds: [ID!]!) {
    deletePeriods(periodIds: $periodIds) {
      id
    }
  }
`;

export const GET_INSTALLED_ENROLLMENT_PERIODS_BY_GROUP_ID = gql`
  query ENROLLMENT_SERVICE_getAllEnrollmentPeriodsByGroupId($groupId: ID!, $showInstalled: Boolean) {
    getAllEnrollmentPeriodsByGroupId(groupId: $groupId, showInstalled: $showInstalled) {
      id
      groupId
      effectiveDate
      productLineIds
      stage
      installedProposal {
        productLines {
          type
          id
          terminationEffectiveDate
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const GET_ALL_RENEWAL_ENROLLMENT_PERIODS_BY_GROUP_ID = gql`
  query ENROLLMENT_SERVICE_getAllRenewalEnrollmentPeriodsByGroupId($groupId: ID!, $showInstalled: Boolean) {
    getAllRenewalEnrollmentPeriodsByGroupId(groupId: $groupId, showInstalled: $showInstalled) {
      id
      groupId
      effectiveDate
      productLineIds
      stage
      installedProposal {
        productLines {
          type
          id
          terminationEffectiveDate
          carrierPortfolioId
          imPortfolioId
          carrierPortfolioName
          additionalSyncData
          products {
            imProductId
            carrierProductId
            carrierProductName
            renewalCarrierProductId
            renewalCarrierProductName
            rates {
              rateStructure
              currentRate
              renewalRate
            }
          }
          exhibitUrls
        }
      }
    }
  }
`;

export const GET_PROPOSAL_CHECKLIST = gql`
  query ENROLLMENT_SERVICE_getProposalChecklist($periodId: ID!, $cartId: String!) {
    getProposalChecklist(periodId: $periodId, cartId: $cartId) {
      checklistOptions {
        card
        buttonState
        completionStatus
      }
    }
  }
`;

export const GET_DOCUMENT_URL = gql`
  query ENROLLMENT_SERVICE_getDocumentUrl($periodId: ID!, $cartId: String!, $fileName: String!, $fileType: String!) {
    getDocumentUrl(periodId: $periodId, cartId: $cartId, fileName: $fileName, fileType: $fileType)
  }
`;

export const TRIGGER_ALL_RENEWAL = gql`
  mutation ENROLLMENT_SERVICE_triggerAllRenewal(
    $sponsorId: String
    $effectiveDate: String
    $sendEmails: Boolean = true
  ) {
    triggerAllRenewal(sponsorId: $sponsorId, effectiveDate: $effectiveDate, sendEmails: $sendEmails)
  }
`;

export const TRIGGER_RENEWALS_BATCH = gql`
  mutation ENROLLMENT_SERVICE_triggerRenewalsBatch($periodIds: [ID!]!, $sendEmails: Boolean = true) {
    triggerRenewalsBatch(periodIds: $periodIds, sendEmails: $sendEmails)
  }
`;

export const ADD_EXHIBIT = gql`
  mutation ENROLLMENT_SERVICE_addExhibit($periodId: ID!, $productLine: String, $fileUrl: String) {
    addExhibit(periodId: $periodId, productLine: $productLine, fileUrl: $fileUrl)
  }
`;

export const UPDATE_EFFECTIVE_DATE = gql`
  mutation ENROLLMENT_SERVICE_updateEffectiveDate($periodId: ID!, $effectiveDate: String!) {
    updateEffectiveDate(periodId: $periodId, effectiveDate: $effectiveDate) {
      id
    }
  }
`;

export const MARK_AS_PREFERRED = gql`
  mutation ENROLLMENT_SERVICE_markAsPreferred($periodId: ID!, $cartId: ID!) {
    markAsPreferred(periodId: $periodId, cartId: $cartId) {
      id
    }
  }
`;

export const UPDATE_INITIATE_FORM_ANSWERS = gql`
  mutation ENROLLMENT_SERVICE_updateInitiateFormAnswers($periodId: ID!, $cartId: String!, $formAnswers: Map!) {
    updateInitiateFormAnswers(periodId: $periodId, cartId: $cartId, formAnswers: $formAnswers) {
      id
    }
  }
`;

export const REMOVE_PROPOSAL_DOCUMENTS = gql`
  mutation ENROLLMENT_SERVICE_removeProposalDocuments($periodId: ID!, $cartId: String!, $documentId: String) {
    removeProposalDocuments(periodId: $periodId, cartId: $cartId, documentId: $documentId)
  }
`;

export const DOWNLOAD_PROPOSAL_DOCUMENT = gql`
  mutation ENROLLMENT_SERVICE_downloadProposalDocument($periodId: ID!, $cartId: String!, $documentId: String) {
    downloadProposalDocument(periodId: $periodId, cartId: $cartId, documentId: $documentId)
  }
`;

export const DOWNLOAD_ALL_PROPOSAL_DOCUMENTS = gql`
  mutation ENROLLMENT_SERVICE_downloadAllProposalDocuments($periodId: ID!, $cartId: String!) {
    downloadAllProposalDocuments(periodId: $periodId, cartId: $cartId)
  }
`;

export const APPLY_PROPOSAL_USER_ACTION = gql`
  mutation ENROLLMENT_SERVICE_applyProposalUserAction($periodId: ID!, $cartId: String!, $userAction: UserActions) {
    applyProposalUserAction(periodId: $periodId, cartId: $cartId, userAction: $userAction) {
      id
    }
  }
`;

export const COPY_RFQ_PROPOSAL = gql`
  mutation ENROLLMENT_SERVICE_copyRFQProposal($periodId: ID!, $cartId: String!, $newCartId: String!) {
    copyRFQProposal(periodId: $periodId, cartId: $cartId, newCartId: $newCartId) {
      id
      proposals {
        cartId
      }
    }
  }
`;

export const SUBMIT_INITIATE_RFQ = gql`
  mutation ENROLLMENT_SERVICE_submitInitiateRFQ($periodId: String!, $cartId: String!, $recipients: [String!]!) {
    submitInitiateRFQ(periodId: $periodId, cartId: $cartId, recipients: $recipients) {
      id
    }
  }
`;

export const PICK_CARRIERS = gql`
  mutation ENROLLMENT_SERVICE_pickCarriers($periodId: String!, $cartId: String!, $productLines: [RFQCarrierByLine!]!) {
    pickCarriers(periodId: $periodId, cartId: $cartId, productLines: $productLines) {
      id
    }
  }
`;

export const SHARE_IMQ = gql`
  mutation ENROLLMENT_SERVICE_shareIMQ($periodId: ID!, $cartId: String!, $recipients: [String!]!) {
    shareIMQ(periodId: $periodId, cartId: $cartId, recipients: $recipients)
  }
`;
